// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import traitApi from './trait.api';
import togglePublishedState from '~/api/togglePublishedState.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchTraitApi = createAsyncThunk(
    'heroSaga/trait/fetchTraitApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await traitApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleTraitPublishedStateApi = createAsyncThunk(
    'heroSaga/trait/toggleTraitPublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue();
        }
    },
);

export const createTraitApi = createAsyncThunk(
    'heroSaga/trait/createTraitApi',
    async ({ navigate, type }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await traitApi.create(type);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeTraitApi = createAsyncThunk(
    'heroSaga/trait/storeTraitApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values, type } = params;

        try {
            dispatch(requestStarted());
            const hero = await traitApi.store(values, type);
            alertSwal('success', 'Trait created successfully!');

            return hero;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showTraitApi = createAsyncThunk(
    'traitSaga/trait/showTraitApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await traitApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateTraitApi = createAsyncThunk(
    'heroSaga/trait/updateTraitApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, id, values, type } = params;

        try {
            dispatch(requestStarted());
            await traitApi.update(id, values, type);
            alertSwal('success', 'Trait updated successfully!');

            return values;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteTraitApi = createAsyncThunk('heroSaga/trait/deleteTraitApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await traitApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const traitSlice = createSlice({
    name: 'heroSaga/trait',
    initialState: { rows: [], types: [], model: {}, editedModel: {}, mainPhases: [], defendingSides: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedTrait(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTraitApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload.traits,
                    types: payload.types,
                };
            })
            .addCase(fetchTraitApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleTraitPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.isReady = !updateRow.isReady;
            })
            .addCase(toggleTraitPublishedStateApi.rejected, () => {
                //do nothing
            });

        builder
            .addCase(createTraitApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.defendingSides = payload.defendingSides;
                state.mainPhases = payload.mainPhases;
            })
            .addCase(createTraitApi.rejected, () => {
                //
            });

        builder
            .addCase(storeTraitApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeTraitApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showTraitApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
                state.defendingSides = payload.defendingSides;
                state.mainPhases = payload.mainPhases;
            })
            .addCase(showTraitApi.rejected, () => {
                //
            });

        builder
            .addCase(updateTraitApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.id);
                updatedRow.name = payload.name;
                updatedRow.description = payload.description;
                updatedRow.boon = state.boons.find((boon) => boon.id === payload.boonId);
                updatedRow.alignment = state.alignments.find((alignment) => alignment.id === payload.alignmentId);
            })
            .addCase(updateTraitApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder.addCase(deleteTraitApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedTrait } = traitSlice.actions;
